import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PolicyModal } from '../components/PolicyModal';
import { FloatingCTA } from '../components/FloatingCTA';
type PolicyType = 'privacy' | 'terms' | 'refund';
export function LegSwellingAdvertorial() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-2410">
      {/* Header */}
      <header className="bg-white border-b border-bone-dark" data-id="element-2411">
        <div className="max-w-4xl mx-auto px-4 py-2" data-id="element-2412">
          <Link to="/" className="flex items-center" data-id="element-2413">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-2414" />
          </Link>
        </div>
      </header>

      {/* Article Container */}
      <article className="max-w-3xl mx-auto px-4 py-6" data-id="element-2415">
        {/* Article Header */}
        <div className="mb-6" data-id="element-2416">
          <h1 className="text-2xl md:text-3xl font-bold text-charcoal leading-tight mb-3" data-id="element-2417">
            I Thought My Legs Were Just Tired — Until I Realized They Were
            Holding Pressure
          </h1>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2418">
            By late afternoon, my legs didn't hurt exactly.
          </p>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2419">
            They just felt… heavier.
          </p>
        </div>

        {/* Article Content */}
        <div className="prose prose-sm max-w-none" data-id="element-2420">
          <div className="space-y-3 text-charcoal leading-relaxed" data-id="element-2421">
            <p className="text-sm" data-id="element-2422">
              Shoes fit tighter. Ankles looked thicker. When I took my socks
              off, the marks stayed longer than they used to.
            </p>

            <p className="text-sm" data-id="element-2423">
              I told myself it was normal. Long days. Sitting too much. Getting
              older.
            </p>

            <p className="text-sm font-semibold" data-id="element-2424">
              But here's what bothered me most:
            </p>

            <p className="text-sm" data-id="element-2425">No pain cream ever helped.</p>

            <p className="text-sm" data-id="element-2426">
              Cold gels. Menthol rubs. "Extra strength" formulas. They all did
              the same thing — made my skin feel numb, then wore off.
            </p>

            <p className="text-sm font-semibold" data-id="element-2427">The heaviness stayed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2428">
              Why Swelling Feels So Hard to Fix
            </h2>

            <p className="text-sm" data-id="element-2429">Most people treat leg swelling like pain.</p>

            <p className="text-sm font-semibold" data-id="element-2430">
              But swelling isn't a sensation problem. It's a movement problem.
            </p>

            <p className="text-sm" data-id="element-2431">
              When circulation slows — from long days, inactivity, stress, or
              time — fluid doesn't move the way it should.
            </p>

            <p className="text-sm" data-id="element-2432">So it stays.</p>

            <p className="text-sm" data-id="element-2433">
              That's why numbing creams don't work. They don't move anything.
              They just distract the nerves.
            </p>

            <p className="text-sm" data-id="element-2434">
              And compression helps… but only while it's on.
            </p>

            <p className="text-sm font-semibold" data-id="element-2435">
              I wanted something that worked with my body — not against it.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2436">
              The Moment That Changed How I Thought About Relief
            </h2>

            <p className="text-sm" data-id="element-2437">A friend said something simple:</p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2438">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2439">
                "Cold shuts things down. Warmth invites things to move."
              </p>
            </div>

            <p className="text-sm" data-id="element-2440">That stuck.</p>

            <p className="text-sm" data-id="element-2441">
              Our bodies associate warmth with circulation. With relaxation.
              With release.
            </p>

            <p className="text-sm" data-id="element-2442">
              So instead of trying another freezing gel, I tried something
              completely different — a botanical oil designed to create gentle,
              controlled warmth.
            </p>

            <p className="text-sm" data-id="element-2443">
              Not heat. Not burn. Just enough warmth to signal circulation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2444">That's when things changed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2445">
              What I Noticed First
            </h2>

            <p className="text-sm" data-id="element-2446">There was no shock.</p>

            <p className="text-sm" data-id="element-2447">No icy blast. No aggressive tingle.</p>

            <p className="text-sm" data-id="element-2448">
              Just a slow warmth that spread instead of attacking.
            </p>

            <p className="text-sm" data-id="element-2449">
              And over time — not instantly, but consistently — my legs felt
              lighter.
            </p>

            <p className="text-sm" data-id="element-2450">Not numb. Not masked.</p>

            <p className="text-sm font-semibold" data-id="element-2451">Supported.</p>

            <p className="text-sm" data-id="element-2452">
              The tightness eased. The end-of-day heaviness didn't build the
              same way.
            </p>

            <p className="text-sm font-semibold" data-id="element-2453">
              For the first time, relief didn't feel like a trick.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2454">
              Why This Works When Creams Don't
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2455">
              <img src="https://cdn.magicpatterns.com/uploads/miu3veA8hGZj2xhtF4swUi/u8896349413_An_educational_anatomical_illustration_showing_mu_40b13d86-9c0d-4e08-a9b7-380274aef750_3.png" alt="Anatomical illustration showing circulation" className="w-full h-auto" loading="lazy" data-id="element-2456" />
            </div>

            <p className="text-sm" data-id="element-2457">
              Most topical products are designed to block sensation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2458">This oil works differently.</p>

            <p className="text-sm" data-id="element-2459">
              It uses specific botanicals — like ginger and black pepper — known
              for encouraging circulation through warmth.
            </p>

            <p className="text-sm" data-id="element-2460">
              Not synthetic heat. Not artificial numbing.
            </p>

            <p className="text-sm" data-id="element-2461">
              Just a signal the body already understands.
            </p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2462">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2463">
                When circulation improves, fluid can move again. When fluid
                moves, pressure can release.
              </p>
            </div>

            <p className="text-sm" data-id="element-2464">That's the part most products ignore.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2465">
              "This Doesn't Feel Like Pain Cream"
            </h2>

            <p className="text-sm" data-id="element-2466">
              That's the phrase I keep hearing from people who try it.
            </p>

            <p className="text-sm" data-id="element-2467">
              Because it doesn't feel like something overpowering your body.
            </p>

            <p className="text-sm font-semibold" data-id="element-2468">
              It feels like something giving your body permission to do what it
              already knows how to do.
            </p>

            <p className="text-sm" data-id="element-2469">People use it on:</p>

            <ul className="space-y-1 my-3" data-id="element-2470">
              <li className="text-sm" data-id="element-2471">Ankles and feet that swell by evening</li>
              <li className="text-sm" data-id="element-2472">Lower legs that feel heavy or tight</li>
              <li className="text-sm" data-id="element-2473">
                Knees and joints that feel stiff after long days
              </li>
              <li className="text-sm" data-id="element-2474">
                Areas where cold products just don't make sense anymore
              </li>
            </ul>

            <p className="text-sm" data-id="element-2475">
              And because it absorbs cleanly, there's no greasy residue, no
              overpowering scent, and no waiting around.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2476">
              The Bigger Shift
            </h2>

            <p className="text-sm" data-id="element-2477">
              What surprised me most wasn't just how my legs felt.
            </p>

            <p className="text-sm" data-id="element-2478">It was how my mindset changed.</p>

            <p className="text-sm" data-id="element-2479">I stopped thinking:</p>

            <p className="text-sm italic" data-id="element-2480">"How do I shut this feeling off?"</p>

            <p className="text-sm" data-id="element-2481">And started thinking:</p>

            <p className="text-sm italic" data-id="element-2482">"How do I support my body better?"</p>

            <p className="text-sm font-semibold" data-id="element-2483">
              That's a very different relationship with relief.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2484">
              The Product Behind the Experience
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2485">
              <img src="https://cdn.magicpatterns.com/uploads/rCeUo34cwDxa2NpcRLnUx1/GINEXT_BOTTLE_.png" alt="GINEXT Ginger Oil bottle" className="w-full h-auto" loading="lazy" data-id="element-2486" />
            </div>

            <p className="text-sm" data-id="element-2487">The oil is called GINEXT.</p>

            <p className="text-sm" data-id="element-2488">
              It's a 100% natural botanical formulation designed to:
            </p>

            <ul className="space-y-1 my-3" data-id="element-2489">
              <li className="text-sm" data-id="element-2490">Create deep, controlled warmth</li>
              <li className="text-sm" data-id="element-2491">
                Support circulation beneath the surface
              </li>
              <li className="text-sm" data-id="element-2492">Avoid numbing or freezing sensations</li>
            </ul>

            <p className="text-sm" data-id="element-2493">
              No menthol overload. No harsh chemicals. No gimmicks.
            </p>

            <p className="text-sm" data-id="element-2494">
              Just something that fits into real life — morning or evening,
              daily or as needed.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2495">
              If This Sounds Familiar
            </h2>

            <p className="text-sm" data-id="element-2496">If your legs:</p>

            <ul className="space-y-1 my-3" data-id="element-2497">
              <li className="text-sm" data-id="element-2498">Feel heavier as the day goes on</li>
              <li className="text-sm" data-id="element-2499">Look more swollen by evening</li>
              <li className="text-sm" data-id="element-2500">Don't respond to cold creams</li>
              <li className="text-sm" data-id="element-2501">Feel tight rather than "painful"</li>
            </ul>

            <p className="text-sm font-semibold" data-id="element-2502">
              This approach may make more sense than what you've already tried.
            </p>

            <div className="bg-white border border-bone-dark rounded-lg p-3 my-4" data-id="element-2503">
              <p className="text-base italic text-charcoal m-0" data-id="element-2504">
                "This doesn't feel like pain cream. It feels like my body
                letting go."
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-bone rounded-xl p-4 my-6 text-center" data-id="element-2505">
              <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-2506">
                Learn how GINEXT supports natural circulation and leg comfort
              </h3>
              <div className="flex flex-col sm:flex-row gap-2 justify-center items-center mt-3" data-id="element-2507">
                <Link to="/#bundles" data-id="element-2508">
                  <motion.button whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="bg-ginger text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-warm hover:shadow-warm-hover transition-all duration-300 inline-flex items-center gap-2" data-id="element-2509">
                    See How GINEXT Works
                    <ArrowRight className="w-4 h-4" data-id="element-2510" />
                  </motion.button>
                </Link>
              </div>

              <p className="text-xs text-charcoal-light mt-3 italic" data-id="element-2511">
                30-day money-back guarantee. No pressure.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 mt-8 text-center text-xs" data-id="element-2512">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2513">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-2514">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-2515">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-2516">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-2517">
              Refund Policy
            </button>
          </div>
          <p data-id="element-2518">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-2519">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-2520" />

      <FloatingCTA ctaText="Support Natural Leg Circulation" ctaLink="/#bundles" subText="Warming botanical relief • 30-Day Guarantee" data-id="element-2521" />
    </div>;
}